import { memo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import styled, { keyframes, css } from 'styled-components'
import Icon from '@/components/icon'
import { show as showVideoLightBox } from '@/components/video-light-box'
import { videoRequest } from '@/models/listing/ports'
import { cleanFormData } from '@/ports/api'
import { colors, breakpoints } from '@/styles'
import { videoHasProcessed } from '@/utils/video'
import { VideoFileThumbnail } from './video-thumbnails'
import VideoPreview from './video-preview'
import { UploadPlaceholder } from './placeholder-items'
import UploadPanel from '../upload-panel'

const postFile = ({ url, data, onUploadProgress }) =>
  new Promise((resolve, reject) => {
    const request = new XMLHttpRequest()
    request.open('POST', url)
    request.upload.addEventListener('progress', e => {
      const progress = Math.round((e.loaded * 100) / e.total)
      onUploadProgress(progress)
    })
    request.addEventListener('load', () => {
      const { status, response } = request
      if (status >= 200 && status < 300) {
        resolve({ status, response })
        return
      }
      reject({ status, response })
    })
    request.addEventListener('error', () => reject())
    request.addEventListener('abort', () => reject())
    request.send(data)
  })

const VideoThumbnail = ({ onRemove, videoLocation, disabled }) => {
  const onPlay = e => {
    showVideoLightBox({
      videoLocation: videoLocation,
      controls: true,
      autoPlay: true
    })
    e.stopPropagation()
  }
  return (
    <VideoPreview onPlay={onPlay} onRemove={onRemove} disabled={disabled}>
      <VideoFileThumbnail sources={videoLocation} disabled={disabled} />
    </VideoPreview>
  )
}

const StyledIcon = styled(Icon)`
  @media screen and (max-width: ${breakpoints.phoneMax}) {
    width: 50px;
    height: 35px;
  }
`

const fadeIn = keyframes`
  from {
    opacity: 0.1;
  }
  to {
    opacity: 1;
  }
`

const ProcessingAnimation = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 5px;

  div {
    display: inline-block;
    width: 8px;
    height: 8px;
    margin: 2px 5px;
    background: ${colors.secondary};
    border-radius: 50%;
    animation: ${fadeIn} 0.6s infinite alternate;
  }

  div:nth-of-type(2) {
    animation-delay: 0.2s;
  }
  div:nth-of-type(3) {
    animation-delay: 0.4s;
  }
`

const uploaderStyle = css`
  padding: 0px;
  box-shadow: none;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    padding: 0;
  }
  border: none;
`

const Placeholder = ({ inProgress, clickProps }) => {
  const { t } = useTranslation()
  return (
    <UploadPlaceholder {...(inProgress ? {} : clickProps)} disabled={inProgress}>
      <StyledIcon id="file_up" width={47} height={55} />
      {inProgress
        ? `${t('b.listing.edit.uploading.text', 'Uploading')}...`
        : t('b.listing.edit.add_video.button', 'Add Video')}
    </UploadPlaceholder>
  )
}

const Processing = () => {
  const { t } = useTranslation()
  return (
    <UploadPlaceholder disabled>
      <StyledIcon id="processing" width={47} height={55} />
      {t('b.listing.edit.processing.text', 'Processing')}
      <ProcessingAnimation>
        <div />
        <div />
        <div />
      </ProcessingAnimation>
    </UploadPlaceholder>
  )
}

export const useProcessing = (processingDelay, uploadedItems) => {
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    if (processing && uploadedItems) {
      setTimeout(() => {
        setProcessing(false)
      }, processingDelay)
    }
  }, [uploadedItems, processing, processingDelay])

  return [processing, setProcessing]
}

const VideoUpload = ({
  items = [],
  unitId,
  listingId,
  onUploadStart,
  onUploadEnd,
  onUploadProgress,
  onUploadError,
  inProgress = false
}) => {
  const dispatch = useDispatch()
  const hasItems = !!items.length
  const processingDelay = 5000
  const [processing, setProcessing] = useProcessing(processingDelay, inProgress, items)
  const isPlaybackDisabled = !items.length || !videoHasProcessed(items[0].created_at)

  const loadFormValues = async () => {
    const {
      body: { formAttributes, formInputs }
    } = await videoRequest(undefined, { unitId })
    return [formAttributes.action, formInputs]
  }

  const onDrop = async files => {
    onUploadStart()
    try {
      const [url, uploadBody] = await loadFormValues()
      const data = cleanFormData({
        ...uploadBody,
        success_action_status: 201,
        file: files[0]
      })
      await postFile({
        url,
        data,
        onUploadProgress
      })
    } catch (e) {
      setProcessing(false)
      onUploadError()
      return
    }
    setProcessing(true)
    onUploadEnd()
  }

  const remove = async item => {
    await dispatch.listing.deleteUnitFile({
      listingId,
      unitId,
      fileId: item.id
    })
  }

  const renderItems = clickProps => {
    if (processing) return <Processing />
    if (hasItems)
      return (
        <>
          {items.map(item => (
            <VideoThumbnail
              key={item.id}
              videoLocation={item.location}
              onRemove={() => remove(item)}
              disabled={isPlaybackDisabled}
            />
          ))}
        </>
      )
    return <Placeholder clickProps={clickProps} inProgress={inProgress} />
  }

  return (
    <UploadPanel
      accept={{
        'video/mpeg': ['.mpeg'],
        'video/mp4': ['.mp4'],
        'video/quicktime': ['.mov', '.qt'],
        'video/x-ms-wmv': ['.wmv'],
        'video/avi': ['.avi'],
        'video/msvideo': ['.avi'],
        'video/x-msvideo': ['.avi']
      }}
      onDrop={onDrop}
      maxFiles={1}
      hasItems={hasItems}
      disabled={inProgress || processing}
      customStyle={uploaderStyle}
      dropOverlayMargin="0px"
    >
      {clickProps => renderItems(clickProps)}
    </UploadPanel>
  )
}

export default memo(VideoUpload)
